import React, { useState, useEffect, useRef, useCallback } from "react";

const EditNutritionModal = ({ isOpen, onClose, onSubmit, initialValues }) => {
  const [formData, setFormData] = useState({
    calories: "",
    proteinPercentage: "",
    carbsPercentage: "",
    fatsPercentage: "",
    sugar: "",
    fiber: "",
  });
  const [calculatedGrams, setCalculatedGrams] = useState({
    protein: 0,
    carbs: 0,
    fats: 0,
  });
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialValues) {
      const calories = parseFloat(initialValues.calories) || 0;
      const proteinGrams = parseFloat(initialValues.proteins) || 0;
      const carbsGrams = parseFloat(initialValues.carbs) || 0;
      const fatsGrams = parseFloat(initialValues.fats) || 0;


      const proteinPercentage =
        calories > 0 ? ((proteinGrams * 4) / calories) * 100 : 0;
      const carbsPercentage =
        calories > 0 ? ((carbsGrams * 4) / calories) * 100 : 0;
      const fatsPercentage =
        calories > 0 ? ((fatsGrams * 9) / calories) * 100 : 0;

      setFormData({
        calories: calories > 0 ? calories.toString() : "",
        proteinPercentage:
          proteinPercentage > 0 ? proteinPercentage.toFixed(1) : "",
        carbsPercentage: carbsPercentage > 0 ? carbsPercentage.toFixed(1) : "",
        fatsPercentage: fatsPercentage > 0 ? fatsPercentage.toFixed(1) : "",
        sugar: initialValues.sugars
          ? parseFloat(initialValues.sugars).toString()
          : "",
        fiber: initialValues.fiber
          ? parseFloat(initialValues.fiber).toString()
          : "",
      });

      setCalculatedGrams({
        protein: proteinGrams,
        carbs: carbsGrams,
        fats: fatsGrams,
      });
    }
  }, [isOpen, initialValues]);

  const calculateGramsFromPercentage = (
    percentage,
    calories,
    caloriesPerGram
  ) => {
    if (!calories || calories === 0) return 0;
    return (calories * (percentage / 100)) / caloriesPerGram;
  };

  const updateCalculatedGrams = (newFormData) => {
    const calories = parseFloat(newFormData.calories) || 0;
    const proteinGrams = calculateGramsFromPercentage(
      parseFloat(newFormData.proteinPercentage) || 0,
      calories,
      4
    );
    const carbsGrams = calculateGramsFromPercentage(
      parseFloat(newFormData.carbsPercentage) || 0,
      calories,
      4
    );
    const fatsGrams = calculateGramsFromPercentage(
      parseFloat(newFormData.fatsPercentage) || 0,
      calories,
      9
    );

    setCalculatedGrams({
      protein: Math.round(proteinGrams * 10) / 10,
      carbs: Math.round(carbsGrams * 10) / 10,
      fats: Math.round(fatsGrams * 10) / 10,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!/^\d*\.?\d*$/.test(value) && value !== "") return;

    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);

    if (name === "calories" || name.includes("Percentage")) {
      updateCalculatedGrams(newFormData);
    }
  };

  const validatePercentages = () => {
    const totalPercentage =
      (parseFloat(formData.proteinPercentage) || 0) +
      (parseFloat(formData.carbsPercentage) || 0) +
      (parseFloat(formData.fatsPercentage) || 0);

    return Math.abs(totalPercentage - 100) <= 0.1;
  };

  const handleSubmit = () => {
    const calories = parseFloat(formData.calories) || 0;

    if (calories === 0) {
      setToastMessage("Please enter a valid calorie amount.");
      setToastType("error");
      setTimeout(() => setToastMessage(""), 3000);
      return;
    }

    if (!validatePercentages()) {
      setToastMessage("Protein, Carbs, and Fats percentages must sum to 100%.");
      setToastType("error");
      setTimeout(() => setToastMessage(""), 3000);
      return;
    }

    const dataToSubmit = {
      calories: calories,
      protein: calculatedGrams.protein,
      carbs: calculatedGrams.carbs,
      fats: calculatedGrams.fats,
      fiber: parseFloat(formData.fiber) || 0,
      sugar: parseFloat(formData.sugar) || 0,
    };

    onSubmit(dataToSubmit);
    closeModal();
  };

  const closeModal = useCallback(() => {
    setFormData({
      calories: "",
      proteinPercentage: "",
      carbsPercentage: "",
      fatsPercentage: "",
      sugar: "",
      fiber: "",
    });
    setCalculatedGrams({ protein: 0, carbs: 0, fats: 0 });
    setToastMessage("");
    setToastType("success");
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "auto";
    };
  }, [closeModal, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {toastMessage && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-md text-white z-[100] ${
            toastType === "error" ? "bg-red-500" : "bg-green-500"
          }`}
        >
          {toastMessage}
        </div>
      )}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"></div>
      <div
        ref={modalRef}
        className="relative bg-white p-8 rounded-xl shadow-2xl w-full max-w-lg z-50 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Edit Nutrition</h2>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 text-2xl w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          {/* Calories */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Total Calories
            </label>
            <div className="relative">
              <input
                type="number"
                name="calories"
                value={formData.calories}
                onChange={handleChange}
                className="w-full p-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                placeholder="Enter calories"
                required
              />
              <span className="absolute right-4 top-4 text-gray-500 font-medium">
                kcal
              </span>
            </div>
          </div>

          {/* Macronutrients */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["Protein", "Carbs", "Fats"].map((macro) => (
              <div
                key={macro}
                className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200"
              >
                <label className="block text-sm font-semibold text-orange-800 mb-2">
                  {macro}
                </label>
                <div className="relative mb-2">
                  <input
                    type="number"
                    name={`${macro.toLowerCase()}Percentage`}
                    value={formData[`${macro.toLowerCase()}Percentage`]}
                    onChange={handleChange}
                    className="w-full p-3 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors appearance-none"
                    placeholder="0"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-orange-600 font-medium">
                    %
                  </span>
                </div>
                <div className="text-sm text-orange-700 bg-orange-100 px-2 py-1 rounded text-center">
                  {calculatedGrams[macro.toLowerCase()]}g
                </div>
              </div>
            ))}
          </div>

          {/* Fiber and Sugar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fiber
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="fiber"
                  value={formData.fiber}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  placeholder="0"
                />
                <span className="absolute right-3 top-3 text-gray-500 font-medium">
                  g
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sugar
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="sugar"
                  value={formData.sugar}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  placeholder="0"
                />
                <span className="absolute right-3 top-3 text-gray-500 font-medium">
                  g
                </span>
              </div>
            </div>
          </div>

          {/* Percentage total */}
          <div className="bg-gray-100 p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Total Percentage:
              </span>
              <span
                className={`text-sm font-bold ${
                  Math.abs(
                    (parseFloat(formData.proteinPercentage) || 0) +
                      (parseFloat(formData.carbsPercentage) || 0) +
                      (parseFloat(formData.fatsPercentage) || 0) -
                      100
                  ) <= 0.1
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {(
                  (parseFloat(formData.proteinPercentage) || 0) +
                  (parseFloat(formData.carbsPercentage) || 0) +
                  (parseFloat(formData.fatsPercentage) || 0)
                ).toFixed(1)}
                %
              </span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="w-full py-4 px-6 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold text-lg hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Update Nutrition
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditNutritionModal;
